import {
    Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Info, Database, RefreshCw, Calendar, Loader2, LifeBuoy } from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { formatRelative } from "@/lib/format";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

export default function DataSourcesDialog({ onRefreshed }) {
    const { user } = useAuth();
    const [open, setOpen] = useState(false);
    const [runs, setRuns] = useState([]);
    const [refreshing, setRefreshing] = useState(false);
    const [reseeding, setReseeding] = useState(false);
    const pollRef = useRef(null);

    const fetchRuns = async () => {
        try {
            const r = await api.get("/intelligence/refresh-runs");
            setRuns(r.data);
            const active = (r.data || []).find((x) => x.status === "running" || x.status === "queued");
            if (active) setRefreshing(true);
            else setRefreshing(false);
            return r.data;
        } catch { return []; }
    };

    useEffect(() => {
        if (!open) {
            if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
            return;
        }
        fetchRuns();
        // Poll every 4s while dialog is open so live progress is visible
        pollRef.current = setInterval(async () => {
            const list = await fetchRuns();
            const hadActive = (list || []).some((x) => x.status === "running" || x.status === "queued");
            if (!hadActive) {
                // refresh just completed — refresh the parent list once
                onRefreshed && onRefreshed();
            }
        }, 4000);
        return () => { if (pollRef.current) clearInterval(pollRef.current); };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]);

    const triggerRefresh = async () => {
        setRefreshing(true);
        try {
            const { data } = await api.post("/intelligence/refresh-all");
            if (data.status === "already_running") {
                toast.info("A refresh is already running — watch progress below");
            } else {
                toast.success("Refresh queued — runs in the background");
            }
            await fetchRuns();
        } catch (e) {
            setRefreshing(false);
            toast.error(e?.response?.data?.detail || "Refresh failed to start");
        }
    };

    const reseedCatalogue = async () => {
        setReseeding(true);
        try {
            const { data } = await api.post("/intelligence/reseed");
            toast.success(`Catalogue restored: ${data.total_drugs} drugs in database`);
            onRefreshed && onRefreshed();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Reseed failed");
        } finally { setReseeding(false); }
    };

    const phaseLabel = (r) => {
        if (r.status === "queued") return "Queued…";
        if (r.status === "failed") return `Failed: ${r.error || "unknown error"}`;
        if (r.status === "completed") return "Completed";
        switch (r.phase) {
            case "discover": return "Phase 1 — discovering new UK pipeline drugs from ClinicalTrials.gov…";
            case "update": return "Phase 2 — updating existing drug profiles…";
            case "intel": return r.intel?.progress
                ? `Phase 3 — regenerating AI intel (${r.intel.progress}/${r.intel.total})`
                : "Phase 3 — regenerating AI intel…";
            default: return "Running…";
        }
    };

    return (
        <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
                <Button variant="ghost" size="sm" className="gap-2 text-[#475569]" data-testid="data-sources-btn">
                    <Info className="w-4 h-4" /> Data sources
                </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-2xl bg-[#F8F7F4]">
                <DialogHeader>
                    <DialogTitle className="font-heading text-[#1A4331]">How this list was built</DialogTitle>
                    <DialogDescription>
                        How the initial drug catalogue was curated and how it stays current.
                    </DialogDescription>
                </DialogHeader>

                <div className="space-y-5 max-h-[60vh] overflow-y-auto">
                    <section>
                        <div className="flex items-center gap-2 mb-2">
                            <Database className="w-4 h-4 text-[#1A4331]" />
                            <h3 className="font-heading font-medium text-[#1A4331]">Initial curation criteria</h3>
                        </div>
                        <p className="text-sm text-[#475569] leading-relaxed mb-3">
                            66 real UK pharmaceutical products were hand-picked from public sources, weighted toward drugs with strong nursing-market relevance.
                        </p>
                        <ul className="space-y-2 text-sm text-[#1E293B]">
                            <li className="flex gap-2"><Badge className="bg-[#1A4331] text-white border-0 hover:bg-[#1A4331] shrink-0">NICE</Badge> Technology Appraisals (TA numbers cited per drug)</li>
                            <li className="flex gap-2"><Badge className="bg-[#1A4331] text-white border-0 hover:bg-[#1A4331] shrink-0">MHRA</Badge> UK approvals & MA holders</li>
                            <li className="flex gap-2"><Badge className="bg-[#1A4331] text-white border-0 hover:bg-[#1A4331] shrink-0">EMA</Badge> Centrally authorised products & filings</li>
                            <li className="flex gap-2"><Badge className="bg-[#1A4331] text-white border-0 hover:bg-[#1A4331] shrink-0">CT.gov</Badge> ClinicalTrials.gov v2 with UK-located studies (Phase I-III)</li>
                            <li className="flex gap-2"><Badge className="bg-[#1A4331] text-white border-0 hover:bg-[#1A4331] shrink-0">BNF</Badge> British National Formulary status</li>
                        </ul>
                    </section>

                    <section>
                        <div className="flex items-center gap-2 mb-2">
                            <Calendar className="w-4 h-4 text-[#1A4331]" />
                            <h3 className="font-heading font-medium text-[#1A4331]">Monthly auto-refresh — 3 phases</h3>
                        </div>
                        <p className="text-sm text-[#475569] leading-relaxed mb-2">
                            On the <strong>1st of each month at 02:00 UTC</strong> (and on demand below), the system runs:
                        </p>
                        <ol className="space-y-1.5 text-sm text-[#1E293B] list-decimal ml-5">
                            <li><strong>Discover</strong> — pulls newly registered UK trials from ClinicalTrials.gov across 15 nursing-relevant clinical areas and inserts any drug not yet in the catalogue.</li>
                            <li><strong>Update</strong> — re-queries each existing drug; updates development stage and UK status when they have changed. Assignments, notes and existing intel are preserved.</li>
                            <li><strong>Intel</strong> — regenerates Claude Sonnet 4.5 spend intelligence for every drug. Failures keep the previous intel intact.</li>
                        </ol>
                    </section>

                    {user?.role === "admin" && (
                        <section>
                            <div className="flex items-center justify-between mb-3 gap-2 flex-wrap">
                                <h3 className="font-heading font-medium text-[#1A4331]">Refresh history</h3>
                                <div className="flex items-center gap-2">
                                    <Button size="sm" variant="outline" onClick={reseedCatalogue} disabled={reseeding}
                                        className="gap-2 border-[#1A4331]/40 text-[#1A4331] hover:bg-[#1A4331] hover:text-white"
                                        data-testid="reseed-btn">
                                        {reseeding ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <LifeBuoy className="w-3.5 h-3.5" />}
                                        Reseed catalogue
                                    </Button>
                                    <Button size="sm" onClick={triggerRefresh} disabled={refreshing}
                                        className="bg-[#1A4331] hover:bg-[#123022] text-white gap-2"
                                        data-testid="manual-refresh-btn">
                                        {refreshing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
                                        Run now
                                    </Button>
                                </div>
                            </div>
                            <p className="text-xs text-[#8A9A86] mb-3 leading-relaxed">
                                <strong>Reseed catalogue</strong> safely re-inserts any of the 66 curated drugs missing from the database (e.g. after a fresh deploy). Never deletes existing data.
                            </p>
                            {runs.length === 0 ? (
                                <p className="text-sm text-[#8A9A86]">No refresh runs yet.</p>
                            ) : (
                                <div className="space-y-2" data-testid="refresh-runs-list">
                                    {runs.slice(0, 5).map((r) => {
                                        const active = r.status === "running" || r.status === "queued";
                                        return (
                                            <div key={r.id} className="p-3 rounded-md bg-white border border-[#E2E8F0] text-sm">
                                                <div className="flex items-center justify-between gap-2 flex-wrap">
                                                    <span className="text-[#1E293B] flex items-center gap-2">
                                                        {active && <Loader2 className="w-3.5 h-3.5 animate-spin text-[#1A4331]" />}
                                                        {r.status === "completed" && formatRelative(r.finished_at)}
                                                        {r.status === "failed" && <Badge className="bg-[#FEE2E2] text-[#991B1B] border-0">Failed</Badge>}
                                                        {active && <Badge className="bg-[#1A4331]/10 text-[#1A4331] border-0">{r.status}</Badge>}
                                                    </span>
                                                    <span className="font-mono text-xs text-[#475569]">
                                                        {r.prune?.pruned_count ? `-${r.prune.pruned_count} pruned · ` : ""}
                                                        {r.discover ? `+${r.discover.newly_added} new` : ""}
                                                        {r.update ? ` · ${r.update.drugs_updated} updated` : ""}
                                                        {r.update?.drugs_deleted ? ` · -${r.update.drugs_deleted} retired` : ""}
                                                        {r.intel ? ` · ${r.intel.succeeded}/${r.intel.total} intel` : ""}
                                                    </span>
                                                </div>
                                                <p className="text-xs text-[#475569] mt-1 leading-relaxed">{phaseLabel(r)}</p>
                                                <p className="text-[10px] text-[#8A9A86] mt-0.5">Triggered by: {r.triggered_by}</p>
                                            </div>
                                        );
                                    })}
                                </div>
                            )}
                        </section>
                    )}
                </div>
            </DialogContent>
        </Dialog>
    );
}
